import React, { useState } from 'react';
import { __ } from 'i18n/index';
import { Request } from 'components/Request';
import PostGroupTopic from 'models/PostGroupTopic';
import PostTopic from 'react/posts/PostTopic';
import PresetRepliesComponent from 'react/posts/PresetRepliesComponent';

const chronologicalTopics = (topics) => [...topics].sort((firstTopic, secondTopic) =>
	Number(firstTopic.time) - Number(secondTopic.time) || Number(firstTopic.id) - Number(secondTopic.id));

// onReplyAndClose adds a "Reply and close" button: it posts the reply like
// "Post reply" and calls onReplyAndClose once the reply is saved.
export default function BugReportDiscussion({ postGroup, inputKey, inputValue, closed = false, canModerate = false, onReplyAndClose = null }) {
	const [topics, setTopics] = useState(() => chronologicalTopics(postGroup.topics));
	const [content, setContent] = useState('');
	const [replying, setReplying] = useState(false);
	const [error, setError] = useState('');
	const [loadingEarlier, setLoadingEarlier] = useState(false);
	const [loadedAll, setLoadedAll] = useState(postGroup.topics.length < 5);
	const composerId = `bug-workspace-reply-${inputValue}`;

	const submitReply = (event, thenClose = false) => {
		event.preventDefault();
		if(replying || !content.trim()) return;
		setReplying(true);
		setError('');
		Request.post(siteUrl + '/posts/submit_post_reply', { [inputKey]: inputValue, message: content })
			.then((response) => {
				if(!response?.reply?.id || typeof response.reply.content !== 'string') throw new Error('Missing reply');
				const reply = PostGroupTopic.create(response.reply);
				setTopics((currentTopics) => chronologicalTopics([...currentTopics, reply]));
				setContent('');
				if(thenClose) onReplyAndClose();
			})
			.catch((failure) => {
				setError(__('Your reply could not be posted. Please try again.'));
				Request.generalJsonError(failure);
			})
			.finally(() => setReplying(false));
	};

	const loadEarlier = () => {
		if(loadingEarlier || loadedAll || !topics.length) return;
		setLoadingEarlier(true);
		setError('');
		Request.get(siteUrl + '/posts/load_more', { last_loaded_post: topics[0].id })
			.then((response) => {
				if(!Array.isArray(response?.posts) || response.posts.some((topic) => !topic?.id || typeof topic.content !== 'string')) throw new Error('Missing replies');
				const earlierTopics = response.posts.map((topic) => PostGroupTopic.create(topic));
				setTopics((currentTopics) => chronologicalTopics([...earlierTopics, ...currentTopics]));
				setLoadedAll(earlierTopics.length < 5);
			})
			.catch((failure) => {
				setError(__('Earlier replies could not be loaded. Please try again.'));
				Request.generalJsonError(failure);
			})
			.finally(() => setLoadingEarlier(false));
	};

	return (
		<section className='bug-workspace-discussion panel' aria-label={__('Discussion')}>
			<header className='bug-workspace-discussion-heading'>
				<h2>{__('Discussion')} <span className='ui-chip'>{topics.length}</span></h2>
				<span>{topics.length ? __('Oldest first') : __('No replies yet')}</span>
			</header>
			{!loadedAll && <button type='button' className='bug-workspace-load-earlier ui-button ui-button--small' disabled={loadingEarlier} onClick={loadEarlier}>{loadingEarlier ? __('Loading…') : __('Load earlier replies')}</button>}
			{topics.length > 0 ? (
				<div className='bug-workspace-replies wall_posts'>
					{topics.map((topic) => <PostTopic key={topic.id} topic={topic} hideComposer />)}
				</div>
			) : (
				<div className='ui-empty' role='status'>
					<span className='ui-empty__icon' aria-hidden='true'><i className='fa fa-comments-o' /></span>
					<div><h3>{__('Start the conversation')}</h3><p>{__('Replies and updates about this report will appear here.')}</p></div>
				</div>
			)}
			{closed && <div className='bug-workspace-closed-note' role='note'><span aria-hidden='true'>✓</span> {__('This report is closed. You can still add a reply.')}</div>}
			{error && <div className='bug-workspace-error' role='alert'>{error}</div>}
			<form className='bug-workspace-composer' onSubmit={submitReply}>
				<label htmlFor={composerId}>{__('Add a reply')}</label>
				<textarea id={composerId} name='message' placeholder={__('Write a reply…')} rows={4} disabled={replying} value={content} onChange={(event) => setContent(event.target.value)} />
				{canModerate && <PresetRepliesComponent draftMode sendPostReply={setContent} replying={replying} />}
				<div className='bug-workspace-composer-footer'>
					<span>{__('Keep replies relevant to this report.')}</span>
					{onReplyAndClose && <button type='button' className='ui-button' disabled={replying || !content.trim()} onClick={(event) => submitReply(event, true)}>{__('Reply and close')}</button>}
					<button type='submit' className='ui-button ui-button--primary' disabled={replying || !content.trim()}>{replying ? __('Posting…') : __('Post reply')} <span aria-hidden='true'>↗</span></button>
				</div>
			</form>
		</section>
	);
}
